import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Full-height centred layout for signed-out pages (reset password, email
 * confirmation, profile completion).
 *
 * @param {object} props
 * @param {React.ReactNode} [props.header] - Optional header above the card (e.g. marketing header).
 * @param {React.ReactNode} props.children - Card(s).
 * @returns {JSX.Element}
 */
export function AuthLayout({ header, children }) {
  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {header}
      <Box
        component="main"
        sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: { xs: 3, sm: 5 } }}
      >
        <Box sx={{ width: '100%', maxWidth: 440 }}>{children}</Box>
      </Box>
    </Box>
  );
}

/**
 * Card for signed-out flows with the Quill AI logo, a title and a subtitle.
 *
 * @param {object} props
 * @param {boolean} [props.logo=true] - Show the Quill AI logo.
 * @param {React.ReactNode} [props.icon] - Status icon shown instead of the logo.
 * @param {React.ReactNode} props.title - Heading (h1).
 * @param {React.ReactNode} [props.subtitle] - Supporting text.
 * @param {React.ReactNode} props.children - Body.
 * @param {'left'|'center'} [props.align='center'] - Header alignment.
 * @returns {JSX.Element}
 */
export function AuthCard({ logo = true, icon, title, subtitle, children, align = 'center' }) {
  return (
    <Box
      sx={{
        p: { xs: 3, sm: 4 },
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card, 16px)',
        boxShadow: 'var(--q-shadow-md)',
      }}
    >
      <Box sx={{ textAlign: align, mb: 3 }}>
        {icon ? (
          <Box sx={{ display: 'flex', justifyContent: align === 'center' ? 'center' : 'flex-start', mb: 1.5 }}>{icon}</Box>
        ) : logo ? (
          <Box
            component="img"
            src="/QuillAI-Logo.png"
            alt="Quill AI Logo"
            sx={{ height: { xs: 80, sm: 96 }, maxWidth: '100%', display: 'inline-block', mb: 1.5 }}
          />
        ) : null}
        <Typography variant="h4" component="h1" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}
